import { FC, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'

import {
  useGetStorageTestConfig,
  useStorageTestList,
  useStorageTestToken,
  useStorageTestUpload
} from '../api/storage-test-API'
import routes from '../routing/routes'
import { currentUserState } from '../utils/state-atoms'

type StepResult = { success?: boolean; [key: string]: unknown }

const toResult = async (request: () => Promise<{ data: StepResult }>): Promise<StepResult> => {
  try {
    return (await request()).data
  } catch (err: any) {
    return {
      success: false,
      httpStatus: err?.response?.status,
      message: err?.response?.data ?? err?.message
    }
  }
}

const ResultPanel: FC<{ title: string; result?: StepResult; showBadge?: boolean }> = ({
  title,
  result,
  showBadge = true
}) => (
  <div className="border rounded p-3 dark:border-neutral-60">
    <div className="flex items-center gap-2 mb-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      {result && showBadge && (
        <span
          className={`px-2 py-0.5 rounded text-xs font-semibold text-white ${
            result.success ? 'bg-green-600' : 'bg-red-600'
          }`}>
          {result.success ? 'PASS' : 'FAIL'}
        </span>
      )}
    </div>
    <pre className="text-xs whitespace-pre-wrap break-all max-h-96 overflow-auto">
      {result ? JSON.stringify(result, null, 2) : 'Not run'}
    </pre>
  </div>
)

const StorageTest: FC = () => {
  const currentUser = useRecoilValue(currentUserState)
  const navigate = useNavigate()

  const [{ data: config }, fetchConfig] = useGetStorageTestConfig()
  const [{ loading: tokenLoading }, runToken] = useStorageTestToken()
  const [{ loading: listLoading }, runList] = useStorageTestList()
  const [{ loading: uploadLoading }, runUpload] = useStorageTestUpload()

  const [prefix, setPrefix] = useState('')
  const [tokenResult, setTokenResult] = useState<StepResult>()
  const [listResult, setListResult] = useState<StepResult>()
  const [uploadResult, setUploadResult] = useState<StepResult>()

  const isAllowed =
    currentUser?.role?.roleId === 'admin' || currentUser?.role?.roleId === 'developer'

  useEffect(() => {
    if (currentUser && !isAllowed) {
      navigate(routes.Dashboard.absolutePath, { replace: true })
    }
  }, [currentUser, isAllowed, navigate])

  useEffect(() => {
    if (isAllowed) fetchConfig().catch(() => undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed])

  const handleToken = async () => setTokenResult(await toResult(runToken))
  const handleList = async () => setListResult(await toResult(() => runList(prefix || undefined)))
  const handleUpload = async () => setUploadResult(await toResult(runUpload))
  const handleRunAll = async () => {
    await handleToken()
    await handleUpload()
    await handleList()
  }

  const busy = tokenLoading || listLoading || uploadLoading

  return (
    <div className="p-4 space-y-4 dark:text-neutral-10">
      <h1 className="text-2xl font-semibold">Cross-Tenant Storage Test</h1>

      <ResultPanel title="Configuration" result={config} showBadge={false} />

      <div className="flex flex-wrap items-end gap-3">
        <Button label="Get Token" onClick={handleToken} disabled={busy} loading={tokenLoading} />
        <Button
          label="Upload Test Blob"
          onClick={handleUpload}
          disabled={busy}
          loading={uploadLoading}
        />
        <div className="flex flex-col">
          <label className="text-sm mb-1">List prefix (optional)</label>
          <InputText value={prefix} onChange={e => setPrefix(e.target.value)} />
        </div>
        <Button label="List Blobs" onClick={handleList} disabled={busy} loading={listLoading} />
        <Button label="Run All" severity="secondary" onClick={handleRunAll} disabled={busy} />
      </div>

      <ResultPanel title="Token" result={tokenResult} />
      <ResultPanel title="Upload" result={uploadResult} />
      <ResultPanel title="List Blobs" result={listResult} />
    </div>
  )
}

export default StorageTest
